import { getTemplateRunResumedFromId } from '@/lib/templateRunChains';
import type { TemplateRunStatus, TemplateRunSummary } from '@/types/templates';

export type TemplateRunStatusTone = 'progress' | 'success' | 'warning' | 'error' | 'neutral';

export type TemplateRunTransitionToast = {
  variant?: 'destructive';
  title: string;
  description: string;
};

type TemplateRunTransitionInput = Pick<TemplateRunSummary, 'status' | 'progress' | 'resume' | 'data'>;

export const TEMPLATE_RUN_STATUS_LABELS: Record<TemplateRunStatus, string> = {
  queued: 'В очереди',
  running: 'Выполняется',
  cancelling: 'Останавливается',
  cancelled: 'Отменен',
  completed: 'Завершен',
  completed_partial: 'Завершен частично',
  failed: 'Ошибка',
};

const TEMPLATE_RUN_STATUS_TONES: Record<TemplateRunStatus, TemplateRunStatusTone> = {
  queued: 'progress',
  running: 'progress',
  cancelling: 'progress',
  cancelled: 'neutral',
  completed: 'success',
  completed_partial: 'warning',
  failed: 'error',
};

const ACTIVE_TEMPLATE_RUN_STATUSES = new Set<string>(['queued', 'running', 'cancelling']);
const TERMINAL_TEMPLATE_RUN_STATUSES = new Set<string>(['completed', 'completed_partial', 'failed', 'cancelled']);

function isKnownTemplateRunStatus(status: string): status is TemplateRunStatus {
  return Object.prototype.hasOwnProperty.call(TEMPLATE_RUN_STATUS_LABELS, status);
}

export function isTemplateRunActiveStatus(status: string | null | undefined): boolean {
  return typeof status === 'string' && ACTIVE_TEMPLATE_RUN_STATUSES.has(status);
}

export function formatTemplateRunStatus(status: string): string {
  return isKnownTemplateRunStatus(status) ? TEMPLATE_RUN_STATUS_LABELS[status] : status;
}

export function resolveTemplateRunStatusTone(status: string): TemplateRunStatusTone {
  return isKnownTemplateRunStatus(status) ? TEMPLATE_RUN_STATUS_TONES[status] : 'neutral';
}

export function resolveTemplateRunNoRowsMessage(status: string): string | null {
  if (status === 'failed') {
    return 'Строки не собраны: запуск завершился с ошибкой.';
  }
  if (status === 'cancelled') {
    return 'Строки не собраны: запуск отменен.';
  }
  if (status === 'completed_partial') {
    return 'Строк не найдено: запуск завершен частично.';
  }
  return null;
}

// Rows the run is known to have collected, or null when its count says nothing:
// a resumed continuation writes into its parent's dataset, so zero rows of its
// own does not mean the dataset is empty.
export function resolveTemplateRunCollectedRows(run: Pick<TemplateRunSummary, 'progress' | 'resume' | 'data'>): number | null {
  const rowsExtracted = run.progress?.rowsExtracted;
  if (typeof rowsExtracted !== 'number' || !Number.isFinite(rowsExtracted)) {
    return null;
  }
  if (rowsExtracted <= 0 && getTemplateRunResumedFromId(run)) {
    return null;
  }
  return rowsExtracted;
}

// The «Остановка/Ошибка на странице N» box explains where pagination stopped. A run
// that completed with no page error simply reached its page limit or the last
// results page, so the box would only repeat the generic completion message.
export function shouldShowTemplateRunStopDetails(run: Pick<TemplateRunSummary, 'status' | 'progress'>): boolean {
  if (run.status === 'completed_partial') {
    return true;
  }
  const lastPage = run.progress?.diagnostics?.lastPage;
  if (!lastPage) {
    return false;
  }
  if (run.status !== 'completed') {
    return true;
  }
  return lastPage.status === 'failed' || Boolean(lastPage.errorMessage) || Boolean(lastPage.errorCode);
}

// A failed run can still own rows from the pages before the error, so only say
// nothing was collected when the row count is known to be zero.
export function resolveTemplateRunFailureRowsNote(rowsCollected: number | null | undefined): string | null {
  if (typeof rowsCollected !== 'number' || !Number.isFinite(rowsCollected)) {
    return null;
  }
  return rowsCollected > 0 ? 'Строки, собранные до ошибки, сохранены.' : 'Данные не собраны.';
}

// Only a run this page has seen in an active status notifies on finish, so
// already-finished runs loaded from history on page open stay silent. Pass
// runLabel when the run is not the one shown in «Результаты».
export function resolveTemplateRunTransitionToast(
  previousStatus: string | null | undefined,
  run: TemplateRunTransitionInput,
  options: { runLabel?: string } = {},
): TemplateRunTransitionToast | null {
  if (!isTemplateRunActiveStatus(previousStatus)) {
    return null;
  }
  const detailsHint = options.runLabel
    ? `Подробности — выберите запуск «${options.runLabel}» в истории запусков.`
    : 'Подробности — в блоке «Результаты».';
  if (run.status === 'failed') {
    return {
      variant: 'destructive',
      title: 'Запуск завершился с ошибкой',
      description: [resolveTemplateRunFailureRowsNote(resolveTemplateRunCollectedRows(run)), detailsHint]
        .filter(Boolean)
        .join(' '),
    };
  }
  if (run.status === 'completed' || run.status === 'completed_partial') {
    const rowsExtracted = run.progress?.rowsExtracted;
    const rowsSummary =
      typeof rowsExtracted === 'number' && Number.isFinite(rowsExtracted) ? `Собрано строк: ${rowsExtracted}.` : null;
    return {
      title: 'Сбор завершен',
      description: [rowsSummary, run.status === 'completed_partial' ? 'Часть страниц не обработана.' : null, detailsHint]
        .filter(Boolean)
        .join(' '),
    };
  }
  return null;
}

// Records the status this page last saw for a run and returns the toast for the
// transition, if any. Overlapping history refreshes can resolve out of order, so
// a terminal status is never replaced by an older active one: the run would look
// live again and announce its finish a second time.
export function trackTemplateRunTransition(
  lastSeenStatuses: Map<string, string>,
  run: TemplateRunTransitionInput & Pick<TemplateRunSummary, 'runId'>,
  options: { runLabel?: string } = {},
): TemplateRunTransitionToast | null {
  const previousStatus = lastSeenStatuses.get(run.runId);
  if (TERMINAL_TEMPLATE_RUN_STATUSES.has(previousStatus ?? '') && isTemplateRunActiveStatus(run.status)) {
    return null;
  }
  lastSeenStatuses.set(run.runId, run.status);
  return resolveTemplateRunTransitionToast(previousStatus, run, options);
}

export function scrollTemplateRunResultsIntoView(sectionId: string): void {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    return;
  }
  window.requestAnimationFrame(() => {
    const section = document.getElementById(sectionId);
    if (section && typeof section.scrollIntoView === 'function') {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
}
